import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { SponsorScroller } from '../components/Sponsors';
import { BlogMenu } from '../components/BlogSlider';
import { ContactForm } from '../components/ContactForm';
import SocialSidebar from '../components/SocialSidebar';
import { DonateButton, GoldOutlineButton } from '../components/DonateButton';
import { ImpactStats } from '../components/ImpactStats';
import { ProgramCard } from '../components/ProgramCard';
import { GoldStarSection } from '../components/GoldStarSection';
import { useReveal, revealClass } from '../utils/useReveal';
import { withBase } from '../utils/apiBase';
import { setCachedBlogs } from '../utils/blogCache';

/* Parallax background layers — slowest (back) to fastest (front). */
const parallaxLayers = [
    { src: '/main_page/main_page_spread_v1.JPG', rate: 0.12, className: 'opacity-100' },
    { src: '/main_page/main_page_spread_v2.jpg', rate: 0.24, className: 'opacity-40' },
    { src: '/main_page/main_page_spread_v3.jpg', rate: 0.38, className: 'opacity-25' },
];

export const Home = () => {
    const layerRefs = useRef([]);

    /* Signature parallax: window scroll → rAF → inline translateY per layer.
       Skipped entirely under prefers-reduced-motion (theme.css also hard-
       neutralizes .oli-parallax-layer transforms as a CSS backstop). */
    useEffect(() => {
        if (
            typeof window.matchMedia === 'function' &&
            window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ) {
            return;
        }

        let frame = null;
        const update = () => {
            frame = null;
            const y = window.scrollY;
            layerRefs.current.forEach((el, i) => {
                if (el) el.style.transform = `translateY(${y * parallaxLayers[i].rate}px)`;
            });
        };
        const onScroll = () => {
            if (frame === null) frame = requestAnimationFrame(update);
        };

        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();
        return () => {
            window.removeEventListener('scroll', onScroll);
            if (frame !== null) cancelAnimationFrame(frame);
        };
    }, []);

    /* Prefetch blog posts so the blog pages render instantly. */
    useEffect(() => {
        const prefetchBlogs = async () => {
            try {
                const response = await fetch(withBase('/blogs'));
                if (!response.ok) return;
                const data = await response.json();
                setCachedBlogs('blogs-all', data);
            } catch (err) {
                console.error('Prefetch blogs failed', err);
            }
        };
        prefetchBlogs();
    }, []);

    const [heroRef, heroVis] = useReveal({ threshold: 0 });
    const [programsRef, programsVis] = useReveal();
    const [eventRef, eventVis] = useReveal();
    const [blogRef, blogVis] = useReveal();
    const [contactRef, contactVis] = useReveal();

    return (
        <div className="bg-offwhite text-ink">
            <SocialSidebar />

            {/* ── Signature parallax hero ─────────────────────────────── */}
            <section className="relative h-screen min-h-[560px] flex items-center justify-center text-center px-4 overflow-hidden bg-forest_deep">
                {parallaxLayers.map((layer, idx) => (
                    <img
                        key={layer.src}
                        ref={(el) => (layerRefs.current[idx] = el)}
                        src={layer.src}
                        alt=""
                        aria-hidden="true"
                        className={`oli-parallax-layer absolute inset-x-0 -top-[12%] h-[124%] w-full object-cover pointer-events-none select-none ${layer.className}`}
                        style={{ zIndex: idx }}
                    />
                ))}
                <div className="absolute inset-0 bg-gradient-to-b from-forest/80 via-forest/70 to-forest_deep z-[5]" aria-hidden="true" />

                <div ref={heroRef} className={revealClass(heroVis, 'relative z-10 max-w-4xl mx-auto')}>
                    <p className="oli-label text-gold_soft mb-5">
                        Operation Liberty Initiative &middot; Illinois 501(c)(3) &middot; Est. 2017
                    </p>
                    <h1
                        className="font-display font-bold text-cream leading-tight mb-6"
                        style={{ fontSize: 'var(--text-hero)' }}
                    >
                        Growing Food. Building Community. Honoring Heroes.
                    </h1>
                    <p className="text-cream/80 text-lg max-w-2xl mx-auto mb-10">
                        Inspired by WWII Victory Gardens, we donate raised garden beds, grow fresh
                        produce for Illinois food pantries, and tend living memorials to our fallen
                        heroes. Grow with us.
                    </p>
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-4">
                        <DonateButton size="lg">Donate</DonateButton>
                        <GoldOutlineButton size="lg" to="/patriotic-signup">Join Patriotic Gardens</GoldOutlineButton>
                    </div>
                </div>

                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 animate-bounce-gentle">
                    <span className="oli-label text-cream/60 text-[10px]">Scroll</span>
                    <svg className="w-5 h-5 text-cream/60" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 14l-7 7m0 0l-7-7" />
                    </svg>
                </div>
            </section>

            {/* ── Impact stats band ───────────────────────────────────── */}
            <ImpactStats />

            {/* ── Meet, Greet & Grow event promo ──────────────────────── */}
            <section
                id="meet-greet-grow"
                className="bg-cream border-y border-line px-4 sm:px-6 lg:px-8"
                style={{ paddingTop: 'var(--space-section)', paddingBottom: 'var(--space-section)' }}
            >
                <div ref={eventRef} className={revealClass(eventVis, 'max-w-7xl mx-auto')}>
                    <div className="oli-paper border border-line rounded-card shadow-card overflow-hidden grid grid-cols-1 md:grid-cols-5 items-stretch">
                        <div className="flex items-center justify-center overflow-hidden bg-[#c8d5c2] md:col-span-2">
                            <img
                                src="/events/meet-greet-grow-banner.png"
                                alt="Meet, Greet and Grow event invitation"
                                className="block h-auto w-full object-contain"
                                style={{ clipPath: 'inset(2px 0 0 0)' }}
                            />
                        </div>

                        <div className="flex flex-col gap-6 p-8 lg:p-12 md:col-span-3">
                            <div className="space-y-3">
                                <p className="oli-label text-gold">Free Community Event — Naperville</p>
                                <h2 className="font-display font-bold text-forest leading-tight" style={{ fontSize: 'var(--text-section)' }}>
                                    Meet, Greet <span className="text-gold">&amp; Grow</span>
                                </h2>
                                <p className="text-forest font-bold text-base sm:text-lg">
                                    Tuesday, October 20, 2026 &bull; 6:00–8:00 PM &bull; Go Brewing, Naperville
                                </p>
                                <p className="text-ink_soft text-base sm:text-lg leading-relaxed">
                                    Join O.L.I. Gardens for an evening of sharing ideas, exploring opportunities, and celebrating successes. Food, snacks, and dessert are provided. New and current members are welcome.
                                </p>
                            </div>

                            <div className="grid grid-cols-2 gap-3 sm:gap-4">
                                <div className="rounded-card border border-line bg-offwhite p-4 text-center">
                                    <svg className="w-5 h-5 text-gold mb-1.5 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-2.761-2.239-5-5-5s-5 2.239-5 5v2m8-13a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    <p className="oli-label text-ink_soft text-[10px] sm:text-xs">Admission</p>
                                    <p className="text-xs sm:text-sm font-bold text-forest">Free Entry</p>
                                </div>
                                <div className="rounded-card border border-line bg-offwhite p-4 text-center">
                                    <svg className="w-5 h-5 text-gold mb-1.5 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                    <p className="oli-label text-ink_soft text-[10px] sm:text-xs">RSVP</p>
                                    <p className="text-xs sm:text-sm font-bold text-forest">Encouraged</p>
                                </div>
                            </div>

                            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                                <Link
                                    to="/meet-greet-grow#register"
                                    className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-btn bg-flag_red hover:bg-flag_red_deep text-white font-bold uppercase tracking-label px-8 py-4 text-sm transition-colors duration-200"
                                >
                                    Register to Attend
                                </Link>
                                <Link
                                    to="/meet-greet-grow"
                                    className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-btn border border-forest/30 text-forest hover:bg-forest/5 font-bold uppercase tracking-label px-8 py-4 text-sm transition-colors duration-200"
                                >
                                    Event Details
                                </Link>
                                <a
                                    href="/events/meet-greet-grow-invitation.jpg"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-btn border border-forest/30 text-forest hover:bg-forest/5 font-bold uppercase tracking-label px-8 py-4 text-sm transition-colors duration-200"
                                >
                                    View Invitation
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── Programs ────────────────────────────────────────────── */}
            <section className="bg-offwhite px-4 sm:px-6 lg:px-8" style={{ paddingTop: 'var(--space-section)', paddingBottom: 'var(--space-section)' }}>
                <div className="max-w-7xl mx-auto">
                    <div className="text-center max-w-2xl mx-auto mb-12 sm:mb-16">
                        <p className="oli-label text-gold mb-3">What We Do</p>
                        <h2 className="font-display font-bold text-forest" style={{ fontSize: 'var(--text-section)' }}>
                            Programs that grow more than food
                        </h2>
                        <p className="text-ink_soft mt-4">
                            From backyard raised beds to pantry shelves across Aurora, Joliet,
                            Rockford, and Naperville — every program plants something that lasts.
                        </p>
                    </div>

                    <div
                        ref={programsRef}
                        className={`oli-stagger ${programsVis ? 'is-revealed' : ''} grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8`}
                    >
                        <ProgramCard
                            eyebrow="Program"
                            title="Raised Garden Beds"
                            image="/gardening.png"
                            imageAlt="Volunteers building a raised garden bed"
                            to="/programs"
                            cta="See the Impact"
                            accent="green"
                        >
                            Your gift builds and fills a raised garden bed for a neighbor in need —
                            fresh vegetables, season after season, grown right at home.
                        </ProgramCard>
                        <ProgramCard
                            eyebrow="Program"
                            title="Youth Internship"
                            image="/youth_intern.jpeg"
                            imageAlt="Youth interns learning sustainable gardening and community leadership"
                            href="mailto:fred@oligardens.org"
                            cta="Email to Apply"
                            accent="gold"
                        >
                            High-school students learn to plan, plant, and harvest community gardens
                            while earning service hours and real leadership experience.
                        </ProgramCard>
                        <ProgramCard
                            eyebrow="Program"
                            title="Food Pantry Donations"
                            image="/main_page/pg_image.jpg"
                            imageAlt="Fresh produce ready for the food pantry"
                            to="/find-food-bank"
                            cta="Find a Food Pantry"
                            accent="red"
                        >
                            Produce from our gardens goes straight to local food pantries, putting
                            fresh, homegrown food on tables across northern Illinois.
                        </ProgramCard>
                    </div>

                    <div className="text-center mt-12">
                        <Link
                            to="/programs"
                            className="inline-flex items-center gap-1.5 font-bold text-forest hover:text-gold transition-colors duration-200 text-sm uppercase tracking-label"
                        >
                            See All Programs
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                            </svg>
                        </Link>
                    </div>
                </div>
            </section>

            {/* ── Gold Star memorial band ─────────────────────────────── */}
            <GoldStarSection />

            {/* ── Blog ────────────────────────────────────────────────── */}
            <section aria-labelledby="home-blog-heading">
                <div className="bg-offwhite text-center px-4 pt-16 sm:pt-20 pb-10">
                    <p className="oli-label text-gold mb-3">From the Garden</p>
                    <h2 id="home-blog-heading" className="font-display font-bold text-forest" style={{ fontSize: 'var(--text-section)' }}>
                        Stories from our gardens
                    </h2>
                </div>
                <div ref={blogRef} className={revealClass(blogVis)}>
                    <BlogMenu />
                </div>
            </section>

            {/* ── Sponsors ────────────────────────────────────────────── */}
            <SponsorScroller />

            {/* ── Contact ─────────────────────────────────────────────── */}
            <section
                id="contact"
                className="bg-gradient-to-b from-forest to-forest_deep px-4 sm:px-6"
                style={{ paddingTop: 'var(--space-section)', paddingBottom: 'var(--space-section)' }}
            >
                <div ref={contactRef} className={revealClass(contactVis, 'max-w-4xl mx-auto')}>
                    <div className="text-center mb-12">
                        <p className="oli-label text-gold_soft mb-3">Get in Touch</p>
                        <h2 className="font-display font-bold text-cream mb-4" style={{ fontSize: 'var(--text-section)' }}>
                            Grow with us.
                        </h2>
                        <p className="text-cream/80">
                            Have questions or want to get involved? Send us a message below.
                        </p>
                    </div>
                    <ContactForm />
                </div>
            </section>
        </div>
    );
};
